import { Color, Vector3 } from 'three'
import Experience from '../Experience.js'
import Environment from './Environment.js'
import Globe from './Globe.js'
// import Doge from './Doge.js'
import Diamonds from './Diamonds.js'
import Donuts from './Donuts.js'
import Spheres from './Spheres.js'
import OuterShapes from './OuterShapes.js'
import Particles from './Particles.js'
import Text from './Text.js'
import SVG from './SVG.js'
// import Plane from './Plane.js'
// import UI from './UI.js'
import gsap from 'gsap'

// The plane the home strings sit in, with the Japanese labels just in front.
const TEXT_Z = -2.5
const LABEL_Z = -2.1

// The home SVGs face the camera, so how far down the frame - and so how far
// from the camera - one lands is the only thing that changes its size on
// screen. These scales are the ones that read correctly at SVG_DISTANCE, and
// mobileSVG() corrects them for wherever the artwork actually ends up.
const SVG_DISTANCE = 8.4
const LABEL_SCALE = 0.001
const LABEL_DROP = 0.014
const BADGE_SCALE = 0.00192

/**
 * The phone layout, written as where each string should land on screen rather
 * than where it sits in the world.
 *
 * A hand-placed world position only ever suits one phone. This camera looks
 * across the text plane rather than at it, so the plane is steeply oblique:
 * the top edge of a portrait screen spans about 13 world units and the bottom
 * edge under 6. Spacing that reads as an even stack low in the frame collapses
 * into a pile higher up, which is what had happened to the welcome block - and
 * the whole stack shifts again on any phone of a different shape. Every entry
 * here is solved back into world space against the live camera by
 * mobileText(), so the one table fits 375x667 through 430x932.
 *
 *   cx, cy  where the string's centre goes, across and down the viewport
 *   cap     how tall its letters should read, as a fraction of viewport width
 *   maxW    how much of the viewport width it may span before it is shrunk
 *
 * cap rather than a width span, because a span is not a size: it is divided by
 * however many characters the string happens to have. Sizing by span left the
 * stack with no hierarchy at all - URALAVERSE! spanning 0.70 came out at a
 * 22px cap on a 375pt screen while WORK spanning 0.31 came out at 28px, so the
 * headline was the smallest thing on the page. cap orders them directly, and
 * maxW is only the guard that keeps the long strings on screen.
 */
const HEADLINE_CX = 0.50

const MOBILE_HOME = {
    // The three headline lines share a centre, so they read as one block
    // rather than three strings that happen to be near each other.
    welcome:    { cx: HEADLINE_CX, cy: 0.245, cap: 0.058, maxW: 0.80 },
    to:         { cx: HEADLINE_CX, cy: 0.310, cap: 0.042, maxW: 0.40 },
    uralaverse: { cx: HEADLINE_CX, cy: 0.375, cap: 0.068, maxW: 0.92 },
    // The links are one rank, so they take one cap between them.
    about:      { cx: 0.40, cy: 0.495, cap: 0.050, maxW: 0.60 },
    contact:    { cx: 0.42, cy: 0.615, cap: 0.050, maxW: 0.70 },
    locations:  { cx: 0.45, cy: 0.735, cap: 0.050, maxW: 0.85 },
    work:       { cx: 0.39, cy: 0.855, cap: 0.050, maxW: 0.50 },
}

// The badge is the one home element that reads better clear of the stack than
// aligned to it, so it takes the top of the frame rather than a slot in the
// table above. An SVG group's origin is its top left, so this is where the
// speech bubble starts, not where the artwork is centred.
const MOBILE_BADGE = { cx: 0.500, cy: 0.012 }

// Where the phone composition takes over. This was 431, which predated the
// iPhone 16 sizes and so dropped the 16 Plus (430 squeaked in) and the 16 Pro
// Max (440) onto the desktop layout. 768 is the point style.css already swaps
// the page over to its mobile assets, so the scene and the DOM now agree.
const MOBILE_MAX_WIDTH = 768

const _camPos = new Vector3()
const _ray = new Vector3()
const _point = new Vector3()
const _foot = new Vector3()

export default class World {
    constructor(params) {
        this.createScene()
        // Wait for resources
        this.resources.on('ready', () => {
            this.intro()
            this.initHome(params)
        })
        // Debug
        this.debug = this.experience.debug
    }

    createScene() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.camera = this.experience.camera.instance
        this.renderer = this.experience.renderer
        this.environment = new Environment()
    }

    intro() {
        gsap.to(this.camera, {
            fov: 75,
            duration: 1.2,
            ease: "power1.inOut",
            // delay: 0.5
        })
        gsap.from(this.camera.position, {
            x: this.isMobile() ? -4 : -5,
            y: this.isMobile() ? -4 : 6,
            z: this.isMobile() ? 8.7 : 6,
            duration: 1,
            ease: "power2.inOut",
            delay: 0.2
        })
    }

    initHome(params) {
        this.particles = new Particles(params.particlesAmount, params.repulsion.particles)
        setTimeout(() => {
            this.diamonds = new Diamonds(params.diamondAmount, params.repulsion.cluster)
            this.donuts = new Donuts(params.donutsAmount, params.repulsion.cluster)
            this.spheres = new Spheres(params.spheresAmount, params.repulsion.cluster)
            this.outerShapes = new OuterShapes(params)
        }, 500)

        // Which of the two compositions is standing, so a resize can tell
        // whether it has crossed the threshold between them.
        this.builtMobile = this.isMobile()

        let tl = gsap.timeline();
        if (this.isMobile()) {
            tl.to(this, {
                onComplete: () => {
                    this.buildMobileHome();
                },
                delay: 1
            })
            .to(this, {
                onComplete: () => {
                    this.buildMobileBadge();
                    // this.ctLogo = new SVG('ctLogo', 'https://jp.cointelegraph.com/', {x: -3.2, y: 9, z: 0}, 0.011);
                },
                delay: 0.2
            });
        } else {
            tl.to(this, {
                onComplete: () => {
                    this.buildDesktopHome();
                },
                delay: 1
            })
            .to(this, {
                onComplete: () => {
                    this.buildDesktopBadge();
                    // this.ctLogo = new SVG('ctLogo', 'https://jp.cointelegraph.com/', {x: -5.8, y: 4, z: 2}, 0.015);
                },
                delay: 0.2
            });
        }
    }

    buildMobileHome() {
        this.welcome = this.mobileText('welcome', 'Welcome', '#002056');
        this.to = this.mobileText('to', 'to the', '#bd4500');
        this.uralaverse = this.mobileText('uralaverse', 'URALAVERSE!', '#520004');
        this.about = this.mobileText('about', 'ABOUT', '#00ff11', 'about');
        this.aboutJP = this.mobileLabel('aboutJP', this.about);
        this.contact = this.mobileText('contact', 'CONTACT', '#bd4500', 'contact');
        this.contactJP = this.mobileLabel('contactJP', this.contact);
        this.locations = this.mobileText('locations', 'LOCATIONS', '#002056', 'locations');
        this.locationsJP = this.mobileLabel('locationsJP', this.locations);
        this.work = this.mobileText('work', 'WORK', '#180052', 'work');
        this.workJP = this.mobileLabel('workJP', this.work);
        this.collectTexts()
    }

    buildMobileBadge() {
        this.uralaLogo = this.mobileSVG(
            'uralaLogo', 'https://www.sortlist.com/agency/urala-communications',
            MOBILE_BADGE.cx, MOBILE_BADGE.cy, 0, BADGE_SCALE);
    }

    buildDesktopHome() {
        this.welcome = new Text('Welcome', 0.9, '#002056', {x: 1, y: 3.5, z: -2.5});
        this.to = new Text('to the', 0.6, '#bd4500', {x: 1, y: 2.4, z: -2.8});
        this.uralaverse = new Text('URALAVERSE!', 0.9, '#520004', {x: 2, y: 1, z: -2.5});
        if (this.debug.active) {
            this.debug.ui.addColor(this.uralaverse.material, 'color').onChange((value) => {
                this.uralaverse.material = value;
            });
        }
        this.about = new Text('ABOUT', 0.4, '#00ff11', {x: -3, y: -1, z: -2}, 'about');
        this.aboutJP = new SVG('aboutJP', '', {x: -3, y: -1.2, z: -2.2}, 0.001);
        this.contact = new Text('CONTACT', 0.5, '#bd4500', {x: 5, y: -2.3, z: -2.5}, 'contact');
        this.contactJP = new SVG('contactJP', '', {x: 5.6, y: -2.65, z: -2.7}, 0.001);
        this.locations = new Text('LOCATIONS', 0.4, '#002056', {x: 5.5, y: -1, z: -2}, 'locations');
        this.locationsJP = new SVG('locationsJP', '', {x: 5.9, y: -1.3, z: -2.2}, 0.001);
        this.locationsJP.model.rotation.x = 0.33;
        this.work = new Text('WORK', 0.4, '#180052', {x: -2.4, y: -2.3, z: -2.2}, 'work');
        this.workJP = new SVG('workJP', '', {x: -2.4, y: -2.5, z: -2.4}, 0.001);
        this.collectTexts()
    }

    buildDesktopBadge() {
        this.uralaLogo = new SVG('uralaLogo', 'https://www.sortlist.com/agency/urala-communications', {x: 6.2, y: 4, z: 0}, 0.005);
    }

    /**
     * Rebuild the home composition when the viewport crosses the phone
     * threshold. The two layouts are different arrangements, not one arrangement
     * at two scales, so which one is standing is decided once when the strings
     * are built - and dragging a window narrow, or switching a device
     * emulator on, used to leave the desktop version squeezed into a phone
     * frame until the page was reloaded.
     */
    resize() {
        // Dragging a window across the threshold fires a stream of events, and
        // the rebuild is not cheap, so settle first.
        clearTimeout(this.relayout)
        this.relayout = setTimeout(() => this.applyLayout(), 150)
    }

    applyLayout() {
        if (!this.texts || this.isMobile() === this.builtMobile) return

        // Whatever the home is doing now, the replacement should carry on
        // doing: the sections hide these while they are open.
        const visible = this.welcome.mesh.visible
        this.disposeHome()
        this.builtMobile = this.isMobile()

        if (this.builtMobile) {
            this.buildMobileHome()
            this.buildMobileBadge()
        } else {
            this.buildDesktopHome()
            this.buildDesktopBadge()
        }

        if (!visible) this.hideHome()
    }

    /** Drop the home strings, their labels and the badge. */
    disposeHome() {
        const raycaster = this.experience.raycaster
        const targets = raycaster.objectsToTest
        // Whatever the cursor was last over is about to stop existing.
        raycaster.currentIntersect = null
        const drop = (object) => {
            const index = targets.indexOf(object)
            if (index !== -1) targets.splice(index, 1)
            gsap.killTweensOf(object.position)
            gsap.killTweensOf(object.rotation)
            object.traverse(child => {
                if (!child.isMesh && !child.isInstancedMesh) return
                child.geometry.dispose()
                child.material.dispose()
            })
            this.scene.remove(object)
        }

        this.texts.forEach(text => drop(text.mesh))
        ;[this.aboutJP, this.contactJP, this.locationsJP, this.workJP, this.uralaLogo]
            .forEach(svg => svg && drop(svg.model))
        this.texts = null
    }

    /**
     * The world point in a given z plane that lands on a given spot on screen,
     * cx and cy being fractions of the viewport, left to right and top to
     * bottom. Read off the live camera, which by the time the home strings are
     * built has finished its intro and is where it will stay.
     */
    screenToPlane(cx, cy, z) {
        const camera = this.camera
        camera.updateMatrixWorld(true)
        _camPos.setFromMatrixPosition(camera.matrixWorld)
        _ray.set(cx * 2 - 1, 1 - cy * 2, 0.5).unproject(camera).sub(_camPos).normalize()
        return _camPos.clone().addScaledVector(_ray, (z - _camPos.z) / _ray.z)
    }

    /** Where a world point lands on screen, in the same fractions. */
    planeToScreen(point) {
        _point.copy(point).project(this.camera)
        return { x: _point.x * 0.5 + 0.5, y: 0.5 - _point.y * 0.5 }
    }

    /**
     * Fractions of the viewport width covered by one world unit along x, at a
     * point. Measured locally rather than taken as a constant because the text
     * plane is oblique: the same span covers far more screen low in the frame
     * than high in it.
     */
    widthPerUnit(point) {
        const camera = this.camera
        const left = _point.copy(point).project(camera).x
        const right = _point.copy(point).setX(point.x + 1).project(camera).x
        return Math.abs(right - left) * 0.5
    }

    /**
     * The same along y, for solving a string's size from its cap height, and
     * given as a fraction of the viewport WIDTH like widthPerUnit - hence the
     * aspect term. Width is the right basis for both: the phone stack is
     * always portrait, and pinning cap heights to the height instead would let
     * the links grow with a tall screen while the headline stayed pinned by
     * the width guard, which is how the hierarchy inverted in the first place.
     */
    heightPerUnit(point) {
        const camera = this.camera
        const sizes = this.experience.sizes
        const bottom = _point.copy(point).project(camera).y
        const top = _point.copy(point).setY(point.y + 1).project(camera).y
        return Math.abs(top - bottom) * 0.5 * (sizes.height / sizes.width)
    }

    /**
     * Where a string of this half-width, centred here, actually lands: how
     * much of the viewport width it spans, and where the middle of that span
     * sits across the viewport.
     *
     * Neither follows from the centre point alone. The plane is oblique, so
     * the end of the string running away from the camera is squeezed and the
     * near end stretched. That makes widthPerUnit at the centre understate a
     * long string - URALAVERSE! solved that way overshot the viewport by a
     * sixth - and it drags the visual middle away from the point the string is
     * centred on, by more for a long string than a short one. Measuring the
     * two ends gives both honestly.
     */
    projectedEnds(centre, halfWidth) {
        const camera = this.camera
        const left = _point.copy(centre).setX(centre.x - halfWidth).project(camera).x
        const right = _point.copy(centre).setX(centre.x + halfWidth).project(camera).x
        return { span: Math.abs(right - left) * 0.5, mid: (left + right) * 0.25 + 0.5 }
    }

    /** One home string, sized and placed to land where MOBILE_HOME asks. */
    mobileText(key, string, color, name) {
        const spec = MOBILE_HOME[key]
        const position = this.screenToPlane(spec.cx, spec.cy, TEXT_Z)
        const glyph = Text.measure(string)

        // Cap height sets the size, because that is what the composition is
        // ordering: the headline should read bigger than the links whatever
        // their strings happen to be.
        let size = spec.cap / (this.heightPerUnit(position) * glyph.height)

        // Then fit the string to the frame: shrink it if it would run off the
        // edge, and slide it so what you see is centred on cx. The two
        // disturb each other - resizing moves both ends, sliding changes how
        // much screen each end covers - and each pass also changes the reading
        // the next correction is made from, so this iterates rather than
        // solving once. It settles in two or three.
        for (let i = 0; i < 6; i++) {
            const ends = this.projectedEnds(position, glyph.width * size * 0.5)
            const drift = spec.cx - ends.mid
            if (ends.span <= spec.maxW && Math.abs(drift) < 0.002) break
            if (ends.span > spec.maxW) size *= spec.maxW / ends.span
            position.x += drift / this.widthPerUnit(position)
        }
        return new Text(string, size, color, position, name)
    }

    /**
     * The Japanese label that belongs under a link. The link is measured
     * rather than offset by hand: the strings come out at different sizes, and
     * a fixed world offset would read as a different gap under each of them.
     */
    mobileLabel(name, link) {
        const foot = _foot.copy(link.mesh.position)
        foot.y += link.bounds.minY
        const screen = this.planeToScreen(foot)
        return this.mobileSVG(name, '', screen.x, screen.y + LABEL_DROP, LABEL_Z, LABEL_SCALE)
    }

    /** An SVG placed on screen and held at the size it reads at correctly. */
    mobileSVG(name, url, cx, cy, z, scale) {
        const position = this.screenToPlane(cx, cy, z)
        const distance = position.distanceTo(this.camera.position)
        return new SVG(name, url, position, scale * distance / SVG_DISTANCE)
    }

    /** The 3D strings, gathered once they exist, for the per-frame swell. */
    collectTexts() {
        this.texts = [
            this.welcome, this.to, this.uralaverse,
            this.about, this.contact, this.locations, this.work
        ]
        // Where each string belongs, so a section that flies one away has
        // somewhere to put it back. The mobile stack is solved from the
        // viewport, so there is no longer a literal to return WORK to.
        this.texts.forEach(text => text.mesh.userData.home = text.mesh.position.clone())
    }

    showHome() {
        this.diamonds.mesh.visible = true
        this.donuts.mesh.visible = true
        this.spheres.mesh.visible = true
        this.outerShapes.setVisible(true)
        this.welcome.mesh.visible = true
        this.to.mesh.visible = true
        this.uralaverse.mesh.visible = true
        this.contact.mesh.visible = true
        this.about.mesh.visible = true
        this.locations.mesh.visible = true
        this.work.mesh.visible = true
        this.contactJP.model.visible = true
        this.aboutJP.model.visible = true
        this.locationsJP.model.visible = true
        this.workJP.model.visible = true
        const workHome = this.work.mesh.userData.home
        gsap.to(this.work.mesh.position, {
            x: workHome.x,
            y: workHome.y,
            z: workHome.z,
            duration: 1.5,
            ease: "back.inOut(1.7)",
        })
        this.uralaLogo.model.visible = true
        // this.ctLogo.model.visible = true
        this.experience.world.environment.pointLight.position.set(1, 3, 10)
        if(this.globe){
            gsap.to(this.globe.model.position, {
                x: 0,
                y: -10,
                z: 0,
                duration: 1.5,
            })
            setTimeout(() => {
                this.globe.model.visible = false
            }, 2000)
        }

        this.renderer.setReinhardTone()
    }

    hideHome() {
        this.diamonds.mesh.visible = false
        this.donuts.mesh.visible = false
        this.spheres.mesh.visible = false
        this.outerShapes.setVisible(false)
        this.welcome.mesh.visible = false
        this.to.mesh.visible = false
        this.uralaverse.mesh.visible = false
        this.contact.mesh.visible = false
        this.about.mesh.visible = false
        this.locations.mesh.visible = false
        this.work.mesh.visible = false
        this.contactJP.model.visible = false
        this.aboutJP.model.visible = false
        this.locationsJP.model.visible = false
        this.workJP.model.visible = false
        this.uralaLogo.model.visible = false
        // this.ctLogo.model.visible = false
    }

    showWork() {
        // this.hideHome()
        this.loadWork();
        this.work.mesh.visible = true
        // this.renderer.setNoTone()
        this.color = new Color('#00c4eb')
        gsap.to(this.work.material.color, 0.5, {
            r: this.color.r,
            g: this.color.g,
            b: this.color.b
        })
        if(this.isMobile()){
            gsap.to(this.work.mesh.position, {
                x: -6,
                y: 8,
                z: 1,
                duration: 1.5,
                ease: "back.inOut(1.3)",
            });
        } else {
            gsap.to(this.work.mesh.position, {
                x: -6,
                y: 8,
                z: 1,
                duration: 1.5,
                ease: "back.inOut(1.3)",
            })
        }
        gsap.to(this.work.mesh.rotation, {
            x: 0,
            y: 1,
            z: 0,
            duration: 1.5,
            ease: "back.inOut(1.3)",
        })
    }

    loadWork() {
        let iframe = document.createElement('iframe');
        iframe.src = "https://uralverse-work-6o7d.vercel.app/";

        // Append the iframe immediately but keep it invisible
        document.getElementById('workFrame').appendChild(iframe);

        // Set timeout to change visibility after 1.5 seconds
        setTimeout(() => {
            iframe.style.visibility = 'visible';
            iframe.style.opacity = 1;
        }, 1400);
    }

    resetWork() {
        const portfolios = [this.portfolio_1, this.portfolio_2, this.portfolio_3, this.portfolio_4, this.portfolio_5, this.portfolio_6];

        for (const portfolio of portfolios) {
            if (typeof portfolio !== 'undefined') {
                if (this.isMobile()) {
                    portfolio.mesh.position.set(portfolio.mesh.userData.mobilePosition.x, portfolio.mesh.userData.mobilePosition.y, portfolio.mesh.userData.mobilePosition.z);
                } else {
                    portfolio.mesh.position.set(portfolio.mesh.userData.position.x, portfolio.mesh.userData.position.y, portfolio.mesh.userData.position.z);
                }

                portfolio.mesh.scale.set(1, 1, 1);
            }
        }
    }


    showLocations() {
        this.hideHome()
        if(typeof this.globe === 'undefined'){
            this.globe = new Globe()
        }
        this.globe.model.visible = true
        gsap.to(this.globe.model.position, {
            x: 0,
            y: 0,
            z: 0,
            duration: 1.5,
        })
        this.lightFollowControls()
    }

    lightFollowControls() {
        let controlsPosition = this.experience.camera.controls.object.position
        this.experience.world.environment.pointLight.position.set(controlsPosition.x, controlsPosition.y, controlsPosition.z)
    }

    /**
     * Whether the phone composition applies. Narrow is not enough on its own:
     * it is a stack of seven rows down 60% of the frame, sized against the
     * width, so a wide short viewport gives it letters far too big for the
     * height it has to put them in - a 740x360 phone held sideways piled the
     * whole stack on top of itself. The desktop composition spreads across the
     * frame instead, which is what a landscape viewport actually has to give.
     */
    isMobile() {
        return window.innerWidth <= MOBILE_MAX_WIDTH && window.innerHeight >= window.innerWidth
    }

    update() {
        if(this.globe && this.globe.model.visible === true){
            this.lightFollowControls()
        }
        if(this.doge) {
            this.doge.update()
        }
        // seconds, clamped so a backgrounded tab doesn't jump the orbits or
        // fire the cursor push off in one step
        const delta = Math.min(this.experience.time.delta, 100) / 1000
        // The shape fields are instanced, so the tweened proxies have to be
        // folded back into the instance matrices each frame. They are built on
        // a timer, hence the guards.
        if(this.diamonds) this.diamonds.update(delta)
        if(this.donuts) this.donuts.update(delta)
        if(this.spheres) this.spheres.update(delta)
        if(this.outerShapes) this.outerShapes.update(delta)
        if(this.particles) this.particles.update(delta)
        if(this.texts) {
            for(let i = 0; i < this.texts.length; i++) this.texts[i].update(delta)
        }
    }
}